/**
 * XYON CUSTOM: Header drawer open/close, L1↔L2 panel transitions, sticky behavior.
 * Follows Concept conventions per CONCEPT_THEME_CONVENTIONS.md.
 */
(function () {
  var section = document.querySelector('.xyon-header-section');
  if (!section) return;

  var hamburger = section.querySelector('[data-drawer-open]');
  var overlay = section.querySelector('[data-drawer-overlay]');
  var drawer = section.querySelector('[data-drawer]');
  var closeBtns = section.querySelectorAll('[data-drawer-close]');
  var l2Triggers = section.querySelectorAll('[data-l2-trigger]');
  var l2Backs = section.querySelectorAll('[data-l2-back]');
  var sentinel = section.querySelector('[data-header-sentinel]');

  var currentL2 = null;
  var closeTimer = null;

  // ── Drawer open / close ──

  function openDrawer() {
    if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
    var scrollbarW = window.innerWidth - document.documentElement.clientWidth;
    section.classList.add('drawer-open');
    hamburger.setAttribute('aria-expanded', 'true');
    drawer.setAttribute('aria-hidden', 'false');
    /* Scroll-lock on <html>, NOT <body>. Setting overflow:hidden on body
       changes the body from being a scroll container, which breaks
       position:sticky on .xyon-header-section — the section reverts to its
       in-flow document position (top of document) and disappears above the
       viewport whenever the drawer is opened from a scrolled position.
       Locking on documentElement preserves the sticky containing-block
       chain. Body still gets the paddingRight compensation because the
       scrollbar gutter belongs to <html>, so its disappearance widens body. */
    document.documentElement.style.overflow = 'hidden';
    if (scrollbarW > 0) document.body.style.paddingRight = scrollbarW + 'px';
    trapFocus(drawer);
  }

  function closeDrawer() {
    section.classList.remove('drawer-open');
    hamburger.setAttribute('aria-expanded', 'false');
    drawer.setAttribute('aria-hidden', 'true');

    // Delay scroll unlock until drawer transition completes (400ms desktop slide)
    closeTimer = setTimeout(function () {
      closeTimer = null;
      if (section.classList.contains('drawer-open')) return;
      document.documentElement.style.overflow = '';
      document.body.style.paddingRight = '';
      if (currentL2) {
        currentL2.classList.remove('is-active');
        currentL2.setAttribute('aria-hidden', 'true');
        currentL2 = null;
      }
    }, 420);

    /* preventScroll: true — the hamburger lives inside the sticky header, so
       a default focus() call asks the browser to "ensure visible" against the
       element's in-flow position (top of document) rather than its sticky
       position, which snaps the page to scrollY=0 on every drawer close.
       Mirrors the pattern already used in trapFocus() below. */
    hamburger.focus({ preventScroll: true });
  }

  hamburger.addEventListener('click', openDrawer);
  overlay.addEventListener('click', closeDrawer);
  closeBtns.forEach(function (btn) { btn.addEventListener('click', closeDrawer); });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && section.classList.contains('drawer-open')) closeDrawer();
  });

  // ── L1 → L2 transitions ──

  function openL2(trigger) {
    var panelId = trigger.getAttribute('data-l2-trigger');
    var panel = section.querySelector('[data-l2-panel="' + panelId + '"]');
    if (!panel) return;

    currentL2 = panel;
    panel.classList.add('is-active');
    panel.setAttribute('aria-hidden', 'false');

    var firstFocusable = panel.querySelector('[data-l2-back]');
    if (firstFocusable) firstFocusable.focus({ preventScroll: true });
  }

  function closeL2() {
    if (!currentL2) return;
    currentL2.classList.remove('is-active');
    currentL2.setAttribute('aria-hidden', 'true');
    currentL2 = null;
  }

  l2Triggers.forEach(function (trigger) {
    trigger.addEventListener('click', function () { openL2(trigger); });
    trigger.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openL2(trigger); }
    });
  });

  l2Backs.forEach(function (back) {
    back.addEventListener('click', closeL2);
    back.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); closeL2(); }
    });
  });

  // ── Focus trap ──

  function trapFocus(container) {
    var focusableEls = container.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), [role="button"]'
    );
    if (focusableEls.length === 0) return;

    var firstEl = focusableEls[0];
    var lastEl = focusableEls[focusableEls.length - 1];

    function handleTab(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
      } else {
        if (document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
      }
    }

    container.addEventListener('keydown', handleTab);
    /* Focus programmatically without visible ring (preventScroll avoids layout shift) */
    firstEl.focus({ preventScroll: true, focusVisible: false });
  }

  // ── Sticky header ──

  if (sentinel && 'IntersectionObserver' in window) {
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          section.classList.toggle('is-sticky', !entry.isIntersecting);
        });
      },
      { threshold: 0 }
    );
    observer.observe(sentinel);
  }

  // ── Header CTA mute state ──
  // Toggles .xyon-header__cta--mute when any primary CTA is in viewport,
  // so the header CTA recedes to a ghost treatment whenever there's
  // another primary action on screen.
  //
  // Selector matches:
  //   .button--primary — every primary button rendered via
  //       snippets/button.liquid, regardless of size (lg, md, sm, default).
  //       The header CTA itself uses .xyon-header__cta — not .button--primary —
  //       so there's no feedback loop.
  //   [data-primary-cta] — manual opt-in for elements not rendered via
  //       the button snippet (custom anchors, etc.)
  //
  // Tracks visible CTAs in a Set (not a counter) so the initial-state
  // callback — which fires once per observed target — counts correctly
  // even when only some are visible at load.
  var headerCta = section.querySelector('.xyon-header__cta');
  if (headerCta && 'IntersectionObserver' in window) {
    var primaryCtas = document.querySelectorAll(
      '.button--primary, [data-primary-cta]'
    );
    if (primaryCtas.length > 0) {
      var visiblePrimaries = new Set();
      var primaryObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              visiblePrimaries.add(entry.target);
            } else {
              visiblePrimaries.delete(entry.target);
            }
          });
          headerCta.classList.toggle('xyon-header__cta--mute', visiblePrimaries.size > 0);
        },
        { threshold: 0 }
      );
      primaryCtas.forEach(function (cta) { primaryObserver.observe(cta); });
    }
  }
})();
